import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI, gmailAPI, companyAPI, driveAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FiLock, FiMail, FiSave, FiUpload, FiTrash2 } from 'react-icons/fi';
import { useTheme, THEMES } from '../context/ThemeContext';
import './Settings.css';

const INP = (extra={}) => ({ width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:14, outline:'none', boxSizing:'border-box', marginTop:4, fontFamily:"'Sora',sans-serif", transition:'border .2s', background:'#fafbfc', ...extra });
const LBL = { display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' };

const Field = ({ label, children }) => (
  <div>
    <label style={LBL}>{label}</label>
    {children}
  </div>
);

const TABS = [
  { id:'profile',      label:'Profile',           icon:'👤', admin:false },
  { id:'appearance',   label:'Appearance',        icon:'🎨', admin:true  },
  { id:'company',      label:'Company & Branding', icon:'🏢', admin:true  },
  { id:'attendance',   label:'Attendance Policy',  icon:'🕘', admin:true  },
  { id:'password',     label:'Password',           icon:'🔐', admin:false },
  { id:'gmail',        label:'Gmail & AI',         icon:'📧', admin:true  },
  { id:'smtp',         label:'Email / SMTP',       icon:'📮', admin:true  },
  { id:'notifications',label:'Notifications',      icon:'🔔', admin:false },
];

const EMPTY_COMPANY = {
  name:'', tagline:'', logoBase64:'', address:'', city:'', country:'India',
  phone:'', email:'', website:'', gstNumber:'', panNumber:'',
  bankName:'', accountNo:'', ifscCode:'', paymentTerms:'Due within 30 days',
  showSalaryToEmployees: false,
};

export default function Settings() {
  const { user, updateUser } = useAuth();
  const { themeId, setTheme, customColor, setCustomTheme, darkMode, toggleDarkMode } = useTheme();
  const [pendingThemeId,     setPendingThemeId]     = useState(themeId);
  const [pendingCustomColor, setPendingCustomColor] = useState(customColor);
  const isAdmin = user?.role === 'admin';
  const [tab,       setTab]       = useState('profile');
  const [loading,   setLoading]   = useState(false);

  // Profile
  const [profileForm, setProfileForm] = useState({ name:user?.name||'', email:user?.email||'', phone:user?.phone||'', company:user?.company||'', position:user?.position||'' });
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '');
  const [avatarSaving,  setAvatarSaving]  = useState(false);
  const avatarRef = useRef();

  const handleAvatarFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5 MB');
    if (!file.type.startsWith('image/')) return toast.error('Please upload an image file');
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const saveAvatar = async () => {
    setAvatarSaving(true);
    try {
      const r = await authAPI.updateAvatar({ avatar: avatarPreview });
      if (r.data.user) updateUser(r.data.user);
      toast.success('Photo updated!');
    } catch { toast.error('Failed to save photo'); }
    setAvatarSaving(false);
  };

  const removeAvatar = async () => {
    setAvatarPreview('');
    setAvatarSaving(true);
    try {
      const r = await authAPI.updateAvatar({ avatar: '' });
      if (r.data.user) updateUser(r.data.user);
      toast.success('Photo removed');
    } catch { toast.error('Failed'); }
    setAvatarSaving(false);
  };

  // Password
  const [pwForm, setPwForm] = useState({ currentPassword:'', newPassword:'', confirmPassword:'' });
  const [showPw, setShowPw] = useState({ current:false, new:false, confirm:false });

  // SMTP
  const [smtpForm, setSmtpForm] = useState({ SMTP_HOST:'', SMTP_PORT:'587', SMTP_USER:'', SMTP_PASS:'', SMTP_FROM:'' });
  const [smtpSaving, setSmtpSaving] = useState(false);

  // Notification Preferences — filtered by role
  const ALL_NOTIF_ITEMS = [
    { key:'task_assigned',   label:'Task assigned to me',       def:true,  roles:['admin','team','client'] },
    { key:'task_status',     label:'Task status changed',       def:true,  roles:['admin','team','client'] },
    { key:'project_update',  label:'Project updated',           def:true,  roles:['admin','team','client'] },
    { key:'task_comment',    label:'New comment on my task',    def:true,  roles:['admin','team','client'] },
    { key:'deadline',        label:'Deadline reminders',        def:true,  roles:['admin','team','client'] },
    { key:'leave_status',    label:'Leave approved / rejected', def:true,  roles:['admin','team'] },
    { key:'invoice_update',  label:'Invoice / payment updates', def:true,  roles:['admin','client'] },
    { key:'daily_digest',    label:'Daily task digest (8 AM)',  def:false, roles:['admin','team'] },
  ];
  const NOTIF_ITEMS = ALL_NOTIF_ITEMS.filter(i => i.roles.includes(user?.role || 'team'));
  const [notifPrefs, setNotifPrefs] = useState(() => {
    const d = {};
    NOTIF_ITEMS.forEach(i => { d[i.key] = i.def; });
    return d;
  });
  const [notifLoaded, setNotifLoaded] = useState(false);

  const loadNotifPrefs = async () => {
    if (notifLoaded) return;
    try {
      const r = await authAPI.getNotificationPrefs();
      const saved = r.data.prefs || {};
      setNotifPrefs(prev => ({ ...prev, ...saved }));
      setNotifLoaded(true);
    } catch { /* use defaults */ setNotifLoaded(true); }
  };

  const saveNotifPrefs = async () => {
    setLoading(true);
    try {
      await authAPI.saveNotificationPrefs({ prefs: notifPrefs });
      toast.success('Notification preferences saved!');
    } catch { toast.error('Failed to save preferences'); }
    setLoading(false);
  };

  // Attendance Policy
  const [attendanceForm, setAttendanceForm] = useState({
    officeStartTime:    '09:00',
    gracePeriodMinutes: 0,
    halfDayAfter:       '12:00',
    autoAbsentAfter:    '18:00',
    maxBreakMinutes:    60,
  });
  const [attendanceLoaded, setAttendanceLoaded] = useState(false);
  const [attendanceSaving, setAttendanceSaving] = useState(false);

  const loadAttendance = async () => {
    if (attendanceLoaded) return;
    try {
      const r = await companyAPI.get();
      const ap = r.data.companySettings?.attendancePolicy || {};
      setAttendanceForm(f => ({ ...f, ...ap }));
      setAttendanceLoaded(true);
    } catch { setAttendanceLoaded(true); }
  };

  const saveAttendance = async (e) => {
    e.preventDefault();
    setAttendanceSaving(true);
    try {
      const r = await companyAPI.get();
      const cs = r.data.companySettings || {};
      await companyAPI.update({ ...cs, attendancePolicy: attendanceForm });
      toast.success('Attendance policy saved!');
    } catch { toast.error('Failed to save'); }
    setAttendanceSaving(false);
  };

  // Company
  const [companyForm,   setCompanyForm]   = useState(EMPTY_COMPANY);
  const [companyLoaded, setCompanyLoaded] = useState(false);
  const [logoPreview,   setLogoPreview]   = useState('');
  const [googleStatus,  setGoogleStatus]  = useState(null); // { connected, gmailConnected, email, gmailEmail }

  useEffect(() => {
    driveAPI.getStatus().then(r => setGoogleStatus(r.data)).catch(() => {});
  }, []);
  const logoRef = useRef();

  const loadCompany = async () => {
    if (companyLoaded) return;
    try {
      const r = await companyAPI.get();
      const cs = r.data.companySettings || {};
      setCompanyForm({ ...EMPTY_COMPANY, ...cs });
      if (cs.logoBase64) setLogoPreview(cs.logoBase64);
      setCompanyLoaded(true);
    } catch { toast.error('Failed to load company settings'); }
  };

  const handleTabChange = (id) => {
    setTab(id);
    if (id === 'company') loadCompany();
    if (id === 'attendance') loadAttendance();
    if (id === 'notifications') loadNotifPrefs();
  };

  // ── Logo upload (convert to base64) ──────────────────────────────────────
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error('Logo must be under 2 MB');
    if (!file.type.startsWith('image/')) return toast.error('Please upload an image file');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target.result;
      setLogoPreview(b64);
      setCompanyForm(f => ({ ...f, logoBase64: b64 }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview('');
    setCompanyForm(f => ({ ...f, logoBase64: '' }));
    if (logoRef.current) logoRef.current.value = '';
  };

  // ── Saves ─────────────────────────────────────────────────────────────────
  const saveProfile = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const r = await authAPI.updateProfile(profileForm); if (r.data.user) updateUser(r.data.user); toast.success('Profile updated!'); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    setLoading(false);
  };

  const saveCompany = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await companyAPI.update(companyForm); toast.success('Company settings saved!'); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    setLoading(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) return toast.error('Passwords do not match');
    if (pwForm.newPassword.length < 6) return toast.error('Minimum 6 characters');
    setLoading(true);
    try {
      const r = await authAPI.changePassword({ currentPassword:pwForm.currentPassword, newPassword:pwForm.newPassword });
      if (r.data.token) localStorage.setItem('token', r.data.token);
      toast.success('Password changed! All other sessions logged out.');
      setPwForm({ currentPassword:'', newPassword:'', confirmPassword:'' });
    }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    setLoading(false);
  };

  const connectGmail = async () => {
    try {
      const r = await gmailAPI.getAuthUrl();
      const popup = window.open(r.data.authUrl, 'gmail_oauth', 'width=500,height=600,scrollbars=yes');
      const handler = (e) => {
        if (e.data?.type === 'GMAIL_CONNECTED') {
          toast.success(`Gmail connected: ${e.data.email}`);
          window.removeEventListener('message', handler);
          driveAPI.getStatus().then(r => setGoogleStatus(r.data)).catch(() => {});
        } else if (e.data?.type === 'GMAIL_ERROR') {
          toast.error(`Gmail error: ${e.data.message}`);
          window.removeEventListener('message', handler);
        }
      };
      window.addEventListener('message', handler);
      const timer = setInterval(() => {
        if (popup?.closed) { clearInterval(timer); window.removeEventListener('message', handler); }
      }, 1000);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to get Gmail auth URL'); }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const S = {
    page:    { fontFamily:"'Sora',sans-serif", paddingBottom:48 },
    layout:  { display:'grid', gridTemplateColumns:'260px 1fr', gap:24, alignItems:'start' },
    sidebar: { background:'#fff', borderRadius:18, padding:16, boxShadow:'0 2px 16px #00000009', border:'1px solid #f1f5f9', position:'sticky', top:24 },
    card:    { background:'#fff', borderRadius:18, padding:32, boxShadow:'0 2px 16px #00000009', border:'1px solid #f1f5f9' },
    tabBtn:  (a) => ({ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', borderRadius:11, border:'none', cursor:'pointer', fontWeight:600, fontSize:13, width:'100%', textAlign:'left', marginBottom:3, transition:'all .18s',
      background: a ? 'var(--pm-gradient)' : 'transparent', color: a ? '#fff' : '#64748b' }),
    btn:     (v) => ({ padding:'10px 20px', borderRadius:10, border:'none', fontWeight:700, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6, transition:'all .18s',
      ...(v==='primary' ? { background:'var(--pm-gradient)', color:'#fff', boxShadow:'0 4px 14px rgba(var(--pm-primary-rgb),0.2)' }
        : v==='danger'  ? { background:'#fee2e2', color:'#ef4444' }
        :                 { background:'#f8fafc', color:'#64748b', border:'1.5px solid #e2e8f0' }) }),
    section: { marginBottom:28 },
    sHead:   { fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14, paddingBottom:10, borderBottom:'1px solid #f1f5f9' },
    grid2:   { display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 },
  };

  return (
    <div style={S.page} className="settings-page">

      <div style={{ marginBottom:28 }}>
        <h1 style={{ margin:0, fontSize:28, fontWeight:800, color:'#0f172a' }}>⚙️ Settings</h1>
        <p style={{ margin:'4px 0 0', color:'#94a3b8' }}>Manage your account, company branding and system preferences</p>
      </div>

      <div style={S.layout}>
        {/* Sidebar */}
        <div style={S.sidebar}>
          <div style={{ textAlign:'center', padding:'14px 0 18px', borderBottom:'1px solid #f1f5f9', marginBottom:10 }}>
            <div style={{ position:'relative', width:52, margin:'0 auto 9px', cursor:'pointer' }} onClick={() => { setTab('profile'); if (avatarRef.current) avatarRef.current.click(); }}>
              {user?.avatar
                ? <img src={user.avatar} alt={user.name} style={{ width:52, height:52, borderRadius:'50%', objectFit:'cover', display:'block' }}/>
                : <div style={{ width:52, height:52, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:22 }}>{user?.name?.charAt(0).toUpperCase()}</div>
              }
              <div style={{ position:'absolute', bottom:0, right:0, width:18, height:18, borderRadius:'50%', background:'#6366f1', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, border:'2px solid #fff' }}>✎</div>
            </div>
            <div style={{ fontWeight:700, fontSize:14, color:'#0f172a' }}>{user?.name}</div>
            <div style={{ fontSize:11, color:'#94a3b8', textTransform:'capitalize', marginTop:2 }}>{user?.role}</div>
          </div>
          {TABS.filter(t => isAdmin || !t.admin).map(t => (
            <button key={t.id} className="settings-stab" style={S.tabBtn(tab===t.id)} onClick={() => handleTabChange(t.id)}>
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div key={tab} style={S.card} className="settings-content">

          {/* ── PROFILE ───────────────────────────────────────────────── */}
          {tab === 'profile' && (
            <form onSubmit={saveProfile}>
              <h2 style={{ margin:'0 0 24px', fontSize:19, fontWeight:800 }}>Profile Information</h2>

              {/* Avatar upload */}
              <div style={{ display:'flex', alignItems:'center', gap:20, marginBottom:28, padding:20, background:'#f8fafc', borderRadius:14, border:'1.5px solid #e2e8f0' }}>
                <div style={{ position:'relative', flexShrink:0 }}>
                  {avatarPreview
                    ? <img src={avatarPreview} alt="avatar" style={{ width:72, height:72, borderRadius:'50%', objectFit:'cover', border:'3px solid #e2e8f0' }}/>
                    : <div style={{ width:72, height:72, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:28 }}>{user?.name?.charAt(0).toUpperCase()}</div>
                  }
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:'#0f172a', marginBottom:4 }}>Profile Photo</div>
                  <div style={{ fontSize:12, color:'#94a3b8', marginBottom:10 }}>JPG, PNG or WebP. Max 5 MB.</div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <button type="button" style={S.btn('primary')} onClick={() => avatarRef.current?.click()}>
                      <FiUpload size={13}/> {avatarPreview ? 'Change Photo' : 'Upload Photo'}
                    </button>
                    {avatarPreview && (
                      <button type="button" style={S.btn('danger')} onClick={removeAvatar} disabled={avatarSaving}>
                        <FiTrash2 size={13}/> Remove
                      </button>
                    )}
                    {avatarPreview !== (user?.avatar || '') && (
                      <button type="button" style={S.btn('primary')} onClick={saveAvatar} disabled={avatarSaving}>
                        <FiSave size={13}/> {avatarSaving ? 'Saving…' : 'Save Photo'}
                      </button>
                    )}
                  </div>
                  <input ref={avatarRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleAvatarFile}/>
                </div>
              </div>

              <div style={{ ...S.grid2, marginBottom:16 }}>
                <div style={{ gridColumn:'1/-1' }}><Field label="Full Name *"><input style={INP()} value={profileForm.name} onChange={e=>setProfileForm(f=>({...f,name:e.target.value}))} required/></Field></div>
                <Field label="Email *"><input type="email" style={INP()} value={profileForm.email} onChange={e=>setProfileForm(f=>({...f,email:e.target.value}))} required/></Field>
                <Field label="Phone"><input style={INP()} value={profileForm.phone} onChange={e=>setProfileForm(f=>({...f,phone:e.target.value}))} placeholder="+91 98765 43210"/></Field>
                <Field label="Company"><input style={INP()} value={profileForm.company} onChange={e=>setProfileForm(f=>({...f,company:e.target.value}))}/></Field>
                <Field label="Position / Role"><input style={INP()} value={profileForm.position} onChange={e=>setProfileForm(f=>({...f,position:e.target.value}))} placeholder="e.g. Senior Developer"/></Field>
              </div>
              <button type="submit" style={S.btn('primary')} disabled={loading}><FiSave/>{loading ? 'Saving...' : 'Save Changes'}</button>
            </form>
          )}

          {/* ── COMPANY & BRANDING ────────────────────────────────────── */}
          {tab === 'company' && isAdmin && (
            <form onSubmit={saveCompany}>
              <h2 style={{ margin:'0 0 6px', fontSize:19, fontWeight:800 }}>Company & Branding</h2>
              <p style={{ margin:'0 0 28px', color:'#94a3b8', fontSize:13 }}>This logo and information appears on all payslips and invoices.</p>

              {/* Logo upload */}
              <div style={S.section}>
                <div style={S.sHead}>Company Logo</div>
                <div style={{ display:'flex', alignItems:'flex-start', gap:24, flexWrap:'wrap' }}>
                  {/* Preview */}
                  <div style={{ width:140, height:100, borderRadius:14, border:`2px dashed ${logoPreview ? 'var(--pm-primary)' : '#e2e8f0'}`,
                    background: logoPreview ? '#f5f3ff' : '#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
                    {logoPreview
                      ? <img src={logoPreview} alt="Logo" style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }}/>
                      : <div style={{ textAlign:'center', color:'#94a3b8' }}><div style={{ fontSize:28, marginBottom:4 }}>🏢</div><div style={{ fontSize:11, fontWeight:600 }}>No logo</div></div>
                    }
                  </div>
                  {/* Upload controls */}
                  <div>
                    <div style={{ fontSize:13, color:'#64748b', marginBottom:12, lineHeight:1.6 }}>
                      Upload your company logo (PNG, JPG, SVG). <br/>Max size: 2 MB. Recommended: 400×200px or wider.
                    </div>
                    <div style={{ display:'flex', gap:10 }}>
                      <button type="button" style={S.btn('primary')} onClick={() => logoRef.current?.click()}>
                        <FiUpload/> {logoPreview ? 'Change Logo' : 'Upload Logo'}
                      </button>
                      {logoPreview && (
                        <button type="button" style={S.btn('danger')} onClick={removeLogo}>
                          <FiTrash2/> Remove
                        </button>
                      )}
                    </div>
                    <input ref={logoRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleLogoUpload}/>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:8 }}>Logo is stored securely and used in all PDF documents.</div>
                  </div>
                </div>
              </div>

              {/* Basic info */}
              <div style={S.section}>
                <div style={S.sHead}>Company Information</div>
                <div style={{ ...S.grid2, rowGap:14 }}>
                  <div style={{ gridColumn:'1/-1' }}><Field label="Company Name *"><input style={INP()} required value={companyForm.name} onChange={e=>setCompanyForm(f=>({...f,name:e.target.value}))} placeholder="Acme Pvt. Ltd."/></Field></div>
                  <Field label="Tagline / Slogan"><input style={INP()} value={companyForm.tagline} onChange={e=>setCompanyForm(f=>({...f,tagline:e.target.value}))} placeholder="Delivering excellence since 2020"/></Field>
                  <Field label="Website"><input style={INP()} value={companyForm.website} onChange={e=>setCompanyForm(f=>({...f,website:e.target.value}))} placeholder="https://acme.com"/></Field>
                  <Field label="Email"><input type="email" style={INP()} value={companyForm.email} onChange={e=>setCompanyForm(f=>({...f,email:e.target.value}))} placeholder="hello@acme.com"/></Field>
                  <Field label="Phone"><input style={INP()} value={companyForm.phone} onChange={e=>setCompanyForm(f=>({...f,phone:e.target.value}))} placeholder="+91 98765 43210"/></Field>
                </div>
              </div>

              {/* Address */}
              <div style={S.section}>
                <div style={S.sHead}>Address</div>
                <div style={{ ...S.grid2, rowGap:14 }}>
                  <div style={{ gridColumn:'1/-1' }}><Field label="Street Address"><input style={INP()} value={companyForm.address} onChange={e=>setCompanyForm(f=>({...f,address:e.target.value}))} placeholder="123, MG Road"/></Field></div>
                  <Field label="City"><input style={INP()} value={companyForm.city} onChange={e=>setCompanyForm(f=>({...f,city:e.target.value}))} placeholder="Mumbai"/></Field>
                  <Field label="Country"><input style={INP()} value={companyForm.country} onChange={e=>setCompanyForm(f=>({...f,country:e.target.value}))} placeholder="India"/></Field>
                </div>
              </div>

              {/* Tax / Legal */}
              <div style={S.section}>
                <div style={S.sHead}>Tax & Legal</div>
                <div style={{ ...S.grid2, rowGap:14 }}>
                  <Field label="GST Number"><input style={INP()} value={companyForm.gstNumber} onChange={e=>setCompanyForm(f=>({...f,gstNumber:e.target.value}))} placeholder="22AAAAA0000A1Z5"/></Field>
                  <Field label="PAN Number"><input style={INP()} value={companyForm.panNumber} onChange={e=>setCompanyForm(f=>({...f,panNumber:e.target.value}))} placeholder="AAAAA9999A"/></Field>
                </div>
              </div>

              {/* Bank details */}
              <div style={S.section}>
                <div style={S.sHead}>Bank Details <span style={{ fontWeight:500, textTransform:'none', letterSpacing:0, color:'#94a3b8', fontSize:11 }}>(shown on invoices)</span></div>
                <div style={{ ...S.grid2, rowGap:14 }}>
                  <Field label="Bank Name"><input style={INP()} value={companyForm.bankName} onChange={e=>setCompanyForm(f=>({...f,bankName:e.target.value}))} placeholder="HDFC Bank"/></Field>
                  <Field label="Account Number"><input style={INP()} value={companyForm.accountNo} onChange={e=>setCompanyForm(f=>({...f,accountNo:e.target.value}))} placeholder="XXXX XXXX XXXX"/></Field>
                  <Field label="IFSC Code"><input style={INP()} value={companyForm.ifscCode} onChange={e=>setCompanyForm(f=>({...f,ifscCode:e.target.value}))} placeholder="HDFC0001234"/></Field>
                  <Field label="Payment Terms"><input style={INP()} value={companyForm.paymentTerms} onChange={e=>setCompanyForm(f=>({...f,paymentTerms:e.target.value}))} placeholder="Due within 30 days"/></Field>
                </div>
              </div>

              {/* Preview badge */}
              <div style={{ background:'#f5f3ff', border:'1.5px solid #e0e7ff', borderRadius:14, padding:20, marginBottom:24, display:'flex', gap:16, alignItems:'center', flexWrap:'wrap' }}>
                {logoPreview
                  ? <img src={logoPreview} alt="logo" style={{ height:48, objectFit:'contain', borderRadius:6 }}/>
                  : <div style={{ width:48, height:48, borderRadius:8, background:'var(--pm-gradient)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:900, fontSize:20 }}>
                      {(companyForm.name||'C').charAt(0)}
                    </div>
                }
                <div>
                  <div style={{ fontWeight:800, fontSize:16, color:'#0f172a' }}>{companyForm.name || 'Your Company'}</div>
                  {companyForm.tagline && <div style={{ fontSize:12, color:'var(--pm-primary)', marginTop:2 }}>{companyForm.tagline}</div>}
                  <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>
                    {[companyForm.city, companyForm.country].filter(Boolean).join(', ')}
                    {companyForm.gstNumber && ` · GST: ${companyForm.gstNumber}`}
                  </div>
                </div>
                <div style={{ marginLeft:'auto', fontSize:11, color:'var(--pm-secondary)', fontWeight:700, background:'#ede9fe', padding:'4px 12px', borderRadius:8 }}>
                  📄 Appears on all documents
                </div>
              </div>

              {/* Employee visibility settings */}
              <div style={{ background:'#f8fafc', border:'1.5px solid #e2e8f0', borderRadius:14, padding:20, marginBottom:24 }}>
                <div style={S.sHead}>Employee Visibility</div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 0', borderBottom:'1px solid #f1f5f9' }}>
                  <div>
                    <div style={{ fontWeight:600, fontSize:14, color:'#1e293b' }}>Show salary to employees</div>
                    <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>When ON, team members can view their own payslips. When OFF, only admins see salary data.</div>
                  </div>
                  <label style={{ position:'relative', display:'inline-block', width:44, height:24, flexShrink:0, marginLeft:16, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!companyForm.showSalaryToEmployees}
                      onChange={e => setCompanyForm(f => ({ ...f, showSalaryToEmployees: e.target.checked }))}
                      style={{ opacity:0, width:0, height:0 }}/>
                    <span style={{
                      position:'absolute', inset:0, borderRadius:24, transition:'.3s',
                      background: companyForm.showSalaryToEmployees ? 'var(--pm-primary)' : '#cbd5e1',
                    }}>
                      <span style={{
                        position:'absolute', top:3, left: companyForm.showSalaryToEmployees ? 23 : 3,
                        width:18, height:18, borderRadius:'50%', background:'#fff',
                        transition:'.3s', boxShadow:'0 1px 3px #0003',
                      }}/>
                    </span>
                  </label>
                </div>
              </div>

              <button type="submit" style={S.btn('primary')} disabled={loading}><FiSave/>{loading ? 'Saving...' : 'Save Company Settings'}</button>
            </form>
          )}

          {/* ── APPEARANCE ───────────────────────────────────────────── */}
          {tab === 'appearance' && (() => {
            const activePending = THEMES.find(t => t.id === pendingThemeId) || THEMES[0];
            const previewPrimary   = pendingThemeId === 'custom' ? pendingCustomColor : activePending.primary;
            const previewSecondary = pendingThemeId === 'custom' ? pendingCustomColor + 'bb' : activePending.secondary;

            const saveAppearance = () => {
              if (pendingThemeId === 'custom') setCustomTheme(pendingCustomColor);
              else setTheme(pendingThemeId);
              toast.success('Theme saved!');
            };

            return (
            <div>
              <h2 style={{ margin:'0 0 6px', fontSize:19, fontWeight:800 }}>Appearance</h2>
              <p style={{ margin:'0 0 28px', color:'#94a3b8', fontSize:13 }}>Choose the primary color theme for the entire app.</p>

              {/* Dark Mode toggle */}
              <div style={{ marginBottom:28, padding:'16px 20px', background: darkMode ? '#1e293b' : '#f8fafc', borderRadius:14, border:'1.5px solid #e2e8f0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color: darkMode ? '#f1f5f9' : '#0f172a' }}>🌙 Dark Mode</div>
                  <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>Switch between light and dark interface</div>
                </div>
                <button onClick={toggleDarkMode} style={{ width:52, height:28, borderRadius:14, border:'none', cursor:'pointer', position:'relative', background: darkMode ? 'var(--pm-primary)' : '#e2e8f0', transition:'all .25s' }}>
                  <div style={{ width:22, height:22, borderRadius:'50%', background:'#fff', position:'absolute', top:3, left: darkMode ? 27 : 3, transition:'all .25s', boxShadow:'0 1px 4px #00000030' }}/>
                </button>
              </div>

              {/* Preset swatches */}
              <div style={{ marginBottom:28 }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14 }}>Preset Themes</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))', gap:10 }}>
                  {THEMES.filter(t => t.id !== 'custom').map(t => {
                    const sel = pendingThemeId === t.id;
                    return (
                      <button key={t.id} onClick={() => setPendingThemeId(t.id)}
                        style={{ border: sel ? `2.5px solid ${t.primary}` : '2px solid #e2e8f0', borderRadius:14, padding:'14px 10px', cursor:'pointer', background: sel ? `${t.primary}0d` : '#fafbfc', display:'flex', flexDirection:'column', alignItems:'center', gap:8, transition:'all .18s', boxShadow: sel ? `0 4px 18px ${t.primary}33` : 'none' }}>
                        <div style={{ display:'flex', gap:4 }}>
                          <div style={{ width:22, height:22, borderRadius:'50%', background:t.primary }}/>
                          <div style={{ width:22, height:22, borderRadius:'50%', background:t.secondary }}/>
                        </div>
                        <span style={{ fontSize:12, fontWeight:700, color: sel ? t.primary : '#475569' }}>{t.label}</span>
                        {sel && <span style={{ fontSize:10, color:t.primary, fontWeight:800 }}>✓ Selected</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom color */}
              <div style={{ background:'#f8fafc', border:'1.5px solid #e2e8f0', borderRadius:14, padding:20, marginBottom:24 }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14 }}>Custom Color</div>
                <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
                  <input type="color" value={pendingCustomColor}
                    onChange={e => { setPendingCustomColor(e.target.value); setPendingThemeId('custom'); }}
                    style={{ width:52, height:52, borderRadius:12, border:'2px solid #e2e8f0', cursor:'pointer', padding:3, background:'#fff' }}/>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#0f172a', marginBottom:4 }}>Pick any color</div>
                    <div style={{ fontSize:12, color:'#94a3b8' }}>
                      Hex: <span style={{ fontWeight:700, color: pendingCustomColor }}>{pendingCustomColor}</span>
                      {pendingThemeId === 'custom' && <span style={{ marginLeft:8, fontSize:11, background:'#f1f5f9', padding:'2px 8px', borderRadius:6, color:'var(--pm-primary)', fontWeight:700 }}>Custom selected</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Live preview */}
              <div style={{ background:'#f8fafc', borderRadius:14, padding:20, border:'1.5px solid #e2e8f0', marginBottom:28 }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14 }}>Preview</div>
                <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
                  <button style={{ padding:'9px 20px', borderRadius:9, border:'none', background:`linear-gradient(135deg,${previewPrimary},${previewSecondary})`, color:'#fff', fontWeight:700, fontSize:13, cursor:'default', boxShadow:`0 4px 14px ${previewPrimary}44` }}>Primary Button</button>
                  <button style={{ padding:'9px 20px', borderRadius:9, border:`2px solid ${previewPrimary}`, background:'transparent', color:previewPrimary, fontWeight:700, fontSize:13, cursor:'default' }}>Outline Button</button>
                  <span style={{ padding:'5px 14px', borderRadius:20, background:`${previewPrimary}18`, color:previewPrimary, fontWeight:700, fontSize:12 }}>Badge</span>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:120, height:8, background:'#e2e8f0', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ width:'65%', height:'100%', background:`linear-gradient(90deg,${previewPrimary},${previewSecondary})`, borderRadius:4 }}/>
                    </div>
                    <span style={{ fontSize:12, color:previewPrimary, fontWeight:700 }}>65%</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
                    <div style={{ width:36, height:36, borderRadius:10, background:`linear-gradient(135deg,${previewPrimary},${previewSecondary})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:800 }}>PM</div>
                    <div style={{ width:36, height:36, borderRadius:'50%', background:`linear-gradient(135deg,${previewPrimary},${previewSecondary})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:800 }}>A</div>
                  </div>
                </div>
              </div>

              {/* Save button */}
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <button onClick={saveAppearance}
                  style={{ padding:'11px 28px', borderRadius:11, border:'none', background:`linear-gradient(135deg,${previewPrimary},${previewSecondary})`, color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer', boxShadow:`0 4px 16px ${previewPrimary}44`, display:'flex', alignItems:'center', gap:8 }}>
                  <FiSave/> Save Theme
                </button>
                {(pendingThemeId !== themeId || (pendingThemeId === 'custom' && pendingCustomColor !== customColor)) && (
                  <span style={{ fontSize:12, color:'#f59e0b', fontWeight:600 }}>⚠ Unsaved changes</span>
                )}
              </div>
            </div>
            );
          })()}

          {/* ── PASSWORD ──────────────────────────────────────────────── */}
          {tab === 'password' && (
            <form onSubmit={changePassword}>
              <h2 style={{ margin:'0 0 24px', fontSize:19, fontWeight:800 }}>Change Password</h2>
              <div style={{ maxWidth:420, display:'flex', flexDirection:'column', gap:16 }}>
                <Field label="Current Password">
                  <div style={{position:'relative'}}>
                    <input type={showPw.current?'text':'password'} style={INP({paddingRight:40})} value={pwForm.currentPassword} onChange={e=>setPwForm(f=>({...f,currentPassword:e.target.value}))} required/>
                    <button type="button" onClick={()=>setShowPw(s=>({...s,current:!s.current}))} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:16,padding:0,marginTop:2}}>{showPw.current?'🙈':'👁'}</button>
                  </div>
                </Field>
                <Field label="New Password">
                  <div style={{position:'relative'}}>
                    <input type={showPw.new?'text':'password'} style={INP({paddingRight:40})} value={pwForm.newPassword} onChange={e=>setPwForm(f=>({...f,newPassword:e.target.value}))} required/>
                    <button type="button" onClick={()=>setShowPw(s=>({...s,new:!s.new}))} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:16,padding:0,marginTop:2}}>{showPw.new?'🙈':'👁'}</button>
                  </div>
                </Field>
                <Field label="Confirm New Password">
                  <div style={{position:'relative'}}>
                    <input type={showPw.confirm?'text':'password'} style={INP({paddingRight:40,borderColor: pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword ? '#ef4444' : undefined})} value={pwForm.confirmPassword} onChange={e=>setPwForm(f=>({...f,confirmPassword:e.target.value}))} required/>
                    <button type="button" onClick={()=>setShowPw(s=>({...s,confirm:!s.confirm}))} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:16,padding:0,marginTop:2}}>{showPw.confirm?'🙈':'👁'}</button>
                  </div>
                  {pwForm.newPassword && pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword &&
                    <div style={{ color:'#ef4444', fontSize:12, marginTop:5, fontWeight:600 }}>⚠ Passwords do not match</div>}
                </Field>
              </div>
              <button type="submit" style={{ ...S.btn('primary'), marginTop:8 }} disabled={loading}><FiLock/>{loading ? 'Changing...' : 'Change Password'}</button>
            </form>
          )}

          {/* ── GMAIL ─────────────────────────────────────────────────── */}
          {tab === 'gmail' && isAdmin && (
            <>
              <h2 style={{ margin:'0 0 8px', fontSize:19, fontWeight:800 }}>Gmail & AI Integration</h2>
              <p style={{ color:'#64748b', margin:'0 0 24px', lineHeight:1.6, fontSize:13 }}>Connect Gmail to auto-create tasks from client emails using AI.</p>

              {/* Gmail status card */}
              <div style={{ background: googleStatus?.gmailConnected ? '#f0fdf4' : '#f5f3ff', border:`1.5px solid ${googleStatus?.gmailConnected ? '#86efac' : '#e0e7ff'}`, borderRadius:14, padding:20, marginBottom:16, display:'flex', alignItems:'center', gap:16 }}>
                <div style={{ fontSize:32 }}>{googleStatus?.gmailConnected ? '✅' : '📧'}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:15 }}>Gmail</div>
                  {googleStatus?.gmailConnected
                    ? <div style={{ fontSize:13, color:'#16a34a', marginTop:2 }}>Connected as <strong>{googleStatus.gmailEmail}</strong></div>
                    : <div style={{ fontSize:13, color:'#64748b', marginTop:2 }}>Not connected</div>}
                </div>
                <button style={S.btn(googleStatus?.gmailConnected ? 'outline' : 'primary')} onClick={connectGmail}>
                  <FiMail/> {googleStatus?.gmailConnected ? 'Reconnect' : 'Connect Gmail'}
                </button>
              </div>

              {/* Drive status card */}
              <div style={{ background: googleStatus?.connected ? '#f0fdf4' : '#f8fafc', border:`1.5px solid ${googleStatus?.connected ? '#86efac' : '#e2e8f0'}`, borderRadius:14, padding:20, marginBottom:20, display:'flex', alignItems:'center', gap:16 }}>
                <div style={{ fontSize:32 }}>{googleStatus?.connected ? '✅' : '💾'}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:15 }}>Google Drive</div>
                  {googleStatus?.connected
                    ? <div style={{ fontSize:13, color:'#16a34a', marginTop:2 }}>Connected as <strong>{googleStatus.email}</strong>{googleStatus.rootFolderUrl && <> · <a href={googleStatus.rootFolderUrl} target="_blank" rel="noreferrer" style={{ color:'#6366f1' }}>Open root folder</a></>}</div>
                    : <div style={{ fontSize:13, color:'#64748b', marginTop:2 }}>Not connected — connecting Gmail also connects Drive</div>}
                </div>
              </div>

              <div style={{ background:'#f5f3ff', borderRadius:14, padding:24, marginBottom:20 }}>
                <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
                  <div style={{ fontSize:36 }}>🤖</div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>AI Email Automation</div>
                    <div style={{ color:'#64748b', fontSize:13, lineHeight:1.6 }}>Claude AI reads Gmail and automatically creates tasks from client emails — matching projects, setting priorities, and assigning team members.</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── SMTP ──────────────────────────────────────────────────── */}
          {tab === 'smtp' && isAdmin && (
            <>
              <h2 style={{ margin:'0 0 6px', fontSize:19, fontWeight:800 }}>Email / SMTP Settings</h2>
              <p style={{ margin:'0 0 24px', color:'#94a3b8', fontSize:13 }}>Configure outgoing email for weekly client reports and notifications. Values are stored in the <code>.env</code> file — restart the server after saving.</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', gridColumn:'1/-1' }}>
                  SMTP Host *
                  <input style={{ display:'block', width:'100%', marginTop:5, padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                    value={smtpForm.SMTP_HOST} onChange={e=>setSmtpForm(f=>({...f,SMTP_HOST:e.target.value}))} placeholder="smtp.gmail.com"/>
                </label>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Port
                  <input style={{ display:'block', width:'100%', marginTop:5, padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                    value={smtpForm.SMTP_PORT} onChange={e=>setSmtpForm(f=>({...f,SMTP_PORT:e.target.value}))} placeholder="587"/>
                </label>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Username / Email *
                  <input style={{ display:'block', width:'100%', marginTop:5, padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                    value={smtpForm.SMTP_USER} onChange={e=>setSmtpForm(f=>({...f,SMTP_USER:e.target.value}))} placeholder="you@gmail.com"/>
                </label>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Password / App Password *
                  <input type="password" style={{ display:'block', width:'100%', marginTop:5, padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                    value={smtpForm.SMTP_PASS} onChange={e=>setSmtpForm(f=>({...f,SMTP_PASS:e.target.value}))} placeholder="Gmail app password"/>
                </label>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', gridColumn:'1/-1' }}>
                  From Name / Address (optional)
                  <input style={{ display:'block', width:'100%', marginTop:5, padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                    value={smtpForm.SMTP_FROM} onChange={e=>setSmtpForm(f=>({...f,SMTP_FROM:e.target.value}))} placeholder='"My Company" <noreply@mycompany.com>'/>
                </label>
              </div>
              <div style={{ marginTop:16, padding:14, background:'#fef9c3', borderRadius:10, fontSize:13, color:'#92400e' }}>
                ⚠️ Add these to your <code>.env</code> file and restart the backend server. Weekly client reports send every <strong>Monday at 9 AM</strong>.
              </div>
              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <button style={{ padding:'10px 24px', background:'linear-gradient(135deg,#0f172a,#1e293b)', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14 }}
                  onClick={async () => {
                    setSmtpSaving(true);
                    try {
                      await import('../services/api').then(m => m.default.post('/settings/smtp', smtpForm));
                      (await import('react-hot-toast')).default.success('SMTP settings saved — restart server to apply');
                    } catch { (await import('react-hot-toast')).default.error('Failed to save'); }
                    setSmtpSaving(false);
                  }}>
                  {smtpSaving ? 'Saving…' : '💾 Save SMTP Settings'}
                </button>
              </div>
            </>
          )}

          {/* ── NOTIFICATIONS ─────────────────────────────────────────── */}
          {tab === 'notifications' && (
            <>
              <h2 style={{ margin:'0 0 6px', fontSize:19, fontWeight:800 }}>Notification Preferences</h2>
              <p style={{ margin:'0 0 24px', color:'#94a3b8', fontSize:13 }}>Choose which notifications you want to receive.</p>
              {/* Email notifications master toggle */}
              {(() => {
                const emailOn = notifPrefs.emailNotifications !== false;
                return (
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 0', borderBottom:'2px solid #e2e8f0', marginBottom:8 }}>
                    <div>
                      <span style={{ fontSize:14, fontWeight:700, color:'#0f172a' }}>📧 Email Notifications</span>
                      <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>Receive email alerts for in-app notifications (requires SMTP setup)</div>
                    </div>
                    <label style={{ position:'relative', display:'inline-block', width:44, height:24, cursor:'pointer', flexShrink:0 }}
                      onClick={() => setNotifPrefs(p => ({ ...p, emailNotifications: !emailOn }))}>
                      <span style={{ position:'absolute', inset:0, background: emailOn ? 'var(--pm-primary)' : '#e2e8f0', borderRadius:12, transition:'background .2s' }}/>
                      <span style={{ position:'absolute', top:2, left: emailOn ? 22 : 2, width:20, height:20, background:'#fff', borderRadius:'50%', transition:'left .2s', boxShadow:'0 1px 4px #0000001a' }}/>
                    </label>
                  </div>
                );
              })()}
              {NOTIF_ITEMS.map(({ key, label }) => {
                const on = !!notifPrefs[key];
                return (
                  <div key={key} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 0', borderBottom:'1px solid #f8fafc' }}>
                    <div>
                      <span style={{ fontSize:14, fontWeight:600, color:'#0f172a' }}>{label}</span>
                    </div>
                    <label style={{ position:'relative', display:'inline-block', width:44, height:24, cursor:'pointer', flexShrink:0 }}
                      onClick={() => setNotifPrefs(p => ({ ...p, [key]: !p[key] }))}>
                      <span style={{ position:'absolute', inset:0, background: on ? 'var(--pm-primary)' : '#e2e8f0', borderRadius:12, transition:'background .2s' }}/>
                      <span style={{ position:'absolute', top:2, left: on ? 22 : 2, width:20, height:20, background:'#fff', borderRadius:'50%', transition:'left .2s', boxShadow:'0 1px 4px #0000001a' }}/>
                    </label>
                  </div>
                );
              })}
              <button style={{ ...S.btn('primary'), marginTop:20 }} onClick={saveNotifPrefs} disabled={loading}>
                <FiSave/>{loading ? 'Saving...' : 'Save Preferences'}
              </button>
            </>
          )}

          {/* ── ATTENDANCE POLICY ─────────────────────────────────────── */}
          {tab === 'attendance' && isAdmin && (
            <form onSubmit={saveAttendance}>
              <h2 style={{ margin:'0 0 6px', fontSize:19, fontWeight:800 }}>🕘 Attendance Policy</h2>
              <p style={{ margin:'0 0 28px', color:'#94a3b8', fontSize:13 }}>
                These settings apply to all employees. Changes take effect on the next check-in.
              </p>

              <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

                <div style={S.grid2}>
                  <Field label="Office Start Time">
                    <input type="time" style={INP()} value={attendanceForm.officeStartTime}
                      onChange={e => setAttendanceForm(f => ({ ...f, officeStartTime: e.target.value }))}/>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>Check-in before this = Present</div>
                  </Field>
                  <Field label="Grace Period (minutes)">
                    <input type="number" min="0" max="120" style={INP()} value={attendanceForm.gracePeriodMinutes}
                      onChange={e => setAttendanceForm(f => ({ ...f, gracePeriodMinutes: Number(e.target.value) }))}/>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>
                      Check-in within grace = Present, after = Late
                      {attendanceForm.gracePeriodMinutes > 0 && (
                        <span style={{ color:'#6366f1', fontWeight:600 }}>
                          {' '}(Late after {(() => {
                            const [h,m] = attendanceForm.officeStartTime.split(':').map(Number);
                            const t = new Date(); t.setHours(h, m + Number(attendanceForm.gracePeriodMinutes), 0);
                            return t.toTimeString().slice(0,5);
                          })()})
                        </span>
                      )}
                    </div>
                  </Field>
                </div>

                <div style={S.grid2}>
                  <Field label="Half-Day Threshold">
                    <input type="time" style={INP()} value={attendanceForm.halfDayAfter}
                      onChange={e => setAttendanceForm(f => ({ ...f, halfDayAfter: e.target.value }))}/>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>Check-in after this time = Half Day</div>
                  </Field>
                  <Field label="Auto-Absent After">
                    <input type="time" style={INP()} value={attendanceForm.autoAbsentAfter}
                      onChange={e => setAttendanceForm(f => ({ ...f, autoAbsentAfter: e.target.value }))}/>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>Mark absent if no check-in by this time</div>
                  </Field>
                </div>

                <div style={S.grid2}>
                  <Field label="Max Break Time (minutes)">
                    <input type="number" min="0" max="480" style={INP()} value={attendanceForm.maxBreakMinutes}
                      onChange={e => setAttendanceForm(f => ({ ...f, maxBreakMinutes: Number(e.target.value) }))}/>
                    <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>
                      Max total break per day. Employees cannot start a new break after this limit.
                      {attendanceForm.maxBreakMinutes > 0 && (
                        <span style={{ color:'#6366f1', fontWeight:600 }}> (Current: {attendanceForm.maxBreakMinutes} min = {(attendanceForm.maxBreakMinutes/60).toFixed(1)}h)</span>
                      )}
                    </div>
                  </Field>
                </div>

                {/* Summary card */}
                <div style={{ background:'#f0fdf4', border:'1.5px solid #bbf7d0', borderRadius:12, padding:'16px 20px' }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'#166534', marginBottom:10 }}>📋 Current Policy Summary</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 24px', fontSize:13, color:'#15803d' }}>
                    <div>✅ <strong>Present</strong> — before {attendanceForm.officeStartTime}
                      {attendanceForm.gracePeriodMinutes > 0 ? ` + ${attendanceForm.gracePeriodMinutes} min grace` : ''}
                    </div>
                    <div>🟡 <strong>Late</strong> — after {(() => {
                      const [h,m] = attendanceForm.officeStartTime.split(':').map(Number);
                      const t = new Date(); t.setHours(h, m + Number(attendanceForm.gracePeriodMinutes), 0);
                      return t.toTimeString().slice(0,5);
                    })()}</div>
                    <div>🟠 <strong>Half Day</strong> — check-in after {attendanceForm.halfDayAfter}</div>
                    <div>❌ <strong>Absent</strong> — no check-in by {attendanceForm.autoAbsentAfter}</div>
                    <div>☕ <strong>Max Break</strong> — {attendanceForm.maxBreakMinutes} min/day</div>
                  </div>
                </div>

              </div>

              <div style={{ marginTop:28 }}>
                <button type="submit" style={S.btn('primary')} disabled={attendanceSaving}>
                  <FiSave/> {attendanceSaving ? 'Saving…' : 'Save Policy'}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
